import React, { useState, useEffect, useRef } from 'react';

interface CarouselSlide {
  id: number;
  src: string;
  alt: string;
  link: string;
  bgColor: string; // Tailwind CSS gradient 
}



const ResponsiveInfiniteCarousel = () => {
  // Sample image data with vibrant colors and gradients for each slide
  const originalSlides = [
    { 
      id: 1, 
      src: "https://naendapi.naecoaching.com/media/images/NAE_COACHING.png", 
      alt: "NAE VISION 2027", 
      link: "#",
      bgColor: "from-purple-600 to-pink-500"
    },
    { 
      id: 2, 
      src: "https://naendapi.naecoaching.com/media/images/NAE_COACHING_2.png", 
      alt: "Better Future", 
      link: "#",
      bgColor: "from-cyan-500 to-blue-500"
    },
    { 
      id: 3, 
      src: "https://naendapi.naecoaching.com/media/images/NAE_COACHING_1.png", 
      alt: "HiTech Learning", 
      link: "#",
      bgColor: "from-green-500 to-emerald-600"
    },
    { 
      id: 4, 
      src: "https://naendapi.naecoaching.com/media/images/NAE_COACHING_3.png", 
      alt: "Our Team", 
      link: "#",
      bgColor: "from-orange-500 to-yellow-500"
    },
    { 
      id: 5, 
      src: "https://naendapi.naecoaching.com/media/images/NAE_COACHING_6.png", 
      alt: "Low Price", 
      link: "#",
      bgColor: "from-indigo-500 to-purple-500"
    }
  ];

  // Create a reference to the slides container
  const slidesRef = useRef<HTMLDivElement>(null);
  const [slides, setSlides] = useState([...originalSlides]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Function to advance to the next slide with infinite forward motion effect
  const nextSlide = () => {
    if (isTransitioning) return;

    setIsTransitioning(true);
    
    // Move to the next slide
    setCurrentIndex(prevIndex => prevIndex + 1);
    
    // When we reach the last visible slide, prepare for the infinite effect
    if (currentIndex === slides.length - 2) {
      // After the transition completes, we'll reset
      setTimeout(() => {
        // Temporarily disable transitions
        if (slidesRef.current) {
          slidesRef.current.style.transition = 'none';
        }
        
        // Reset the index to show the first original slide
        setCurrentIndex(0);
        
        // Re-enable transitions after a brief delay
        setTimeout(() => {
          if (slidesRef.current) {
            slidesRef.current.style.transition = 'transform 700ms ease-in-out';
          }
          setIsTransitioning(false);
        }, 50);
      }, 700); // This should match the transition duration
    } else {
      setTimeout(() => {
        setIsTransitioning(false);
      }, 700);
    }
  };

  // Initialize the carousel with duplicate slides for infinite scrolling
  useEffect(() => {
    // We duplicate the original slides to create the infinite effect
    setSlides([...originalSlides, ...originalSlides]);
  }, []);

  // Auto-sliding functionality
  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        nextSlide();
      }, 3000); // Auto-slide every 3 seconds
      
      return () => clearInterval(interval);
    }
  }, [isPaused, currentIndex, isTransitioning]);
  
  // Handler for manual navigation to specific slide
  const goToSlide = (index: number) => {
    if (isTransitioning) return;
    setCurrentIndex(index);
  };

  // Toggle pause/play
  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  // Calculate which dots to show (only show dots for the original slides)
  const visibleSlideIndex = currentIndex % originalSlides.length;

  return (
    // Main container - fully responsive with no fixed width/height
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
      {/* Colorful header */}
      {/* <div className="text-center mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 bg-clip-text text-transparent">
          Infinite Forward Carousel
        </h2>
        <p className="text-sm sm:text-base text-gray-600">Always moving forward, never backward</p>
      </div> */}
      
      {/* Main carousel container - fully responsive */}
      <div className="relative overflow-hidden rounded-lg sm:rounded-xl shadow-xl">
        {/* Slides container - aspect ratio maintained for all screen sizes */}
        <div 
          ref={slidesRef}
          className="relative flex transition-transform duration-700 ease-in-out w-full"
          style={{ 
            transform: `translateX(-${currentIndex * 100}%)`,
            aspectRatio: "16/9" // Responsive aspect ratio instead of fixed height
          }}
        >
          {slides.map((slide, index) => (
            <div 
              key={`${slide.id}-${index}`}
              className={`w-full flex-shrink-0 bg-gradient-to-r ${slide.bgColor} p-1 lg:p-3 sm:p-6 flex items-center justify-center relative`}
            >
              <a 
                href={slide.link}
                className="block relative w-full h-full overflow-hidden rounded-lg transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              >
                <div className="absolute inset-0 bg-black/30 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <span className="text-white font-bold text-sm sm:text-lg px-4 py-2 bg-black/50 rounded-full">
                    Explore {slide.alt}
                  </span>
                </div>
                <img 
                  src={slide.src} 
                  alt={slide.alt} 
                  className="w-full h-full object-cover"
                />
              </a>
              
              {/* Slide caption with colorful styling */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white/80 backdrop-blur-sm px-3 sm:px-6 py-1 sm:py-2 rounded-full shadow-lg">
                <p className="text-sm sm:text-base font-semibold text-gray-800">{slide.alt}</p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Forward navigation arrow - responsive sizing */}
        <button 
          onClick={nextSlide}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 sm:p-3 rounded-full shadow-lg transition-transform duration-300 hover:scale-110 focus:outline-none"
          aria-label="Next slide"
          disabled={isTransitioning}
        >
          <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
          </svg>
        </button>
        
        {/* Pause/Play button - responsive sizing */}
        <button
          onClick={togglePause}
          className="absolute right-2 sm:right-4 bottom-2 sm:bottom-4 bg-white/90 hover:bg-white text-gray-800 p-1 sm:p-2 rounded-full shadow-lg transition-transform duration-300 hover:scale-110 focus:outline-none"
          aria-label={isPaused ? "Play slideshow" : "Pause slideshow"}
        >
          {isPaused ? (
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"></path>
            </svg>
          ) : (
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"></path>
            </svg>
          )}
        </button>
      </div>
      
      {/* Indicator dots - responsive sizing */}
      <div className="flex justify-center mt-3 sm:mt-6 space-x-2 sm:space-x-3">
        {originalSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-all duration-300 ${
              index === visibleSlideIndex 
                ? `bg-gradient-to-r ${originalSlides[index].bgColor} shadow-md scale-125` 
                : 'bg-gray-300 hover:bg-gray-400'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
      
      {/* Progress indicator - responsive width
      <div className="relative h-1 bg-gray-200 rounded-full mx-auto mt-3 sm:mt-6 max-w-xs sm:max-w-md overflow-hidden">
        <div 
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-purple-500"
          style={{ 
            width: `${(visibleSlideIndex / (originalSlides.length - 1)) * 100}%`,
            transition: 'width 700ms ease-in-out'
          }}
        ></div>
      </div> */}
      
      {/* Auto-slide status indicator - responsive text size
      <div className="flex justify-center mt-2 sm:mt-4 text-xs sm:text-sm">
        <span className={`flex items-center ${isPaused ? 'text-gray-500' : 'text-green-500'}`}>
          {isPaused ? (
            <>
              <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"></path>
              </svg>
              Slideshow paused
            </>
          ) : (
            <>
              <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 animate-pulse" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"></path>
              </svg>
              Always advancing forward
            </>
          )}
        </span>
      </div> */}
    </div>
  );
};

export default ResponsiveInfiniteCarousel;